/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { useSpring, useSprings, animated } from '@react-spring/web';
import { cloneElement, CSSProperties, ReactElement, RefObject, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

const getWindowDimensions = (container?: RefObject<HTMLDivElement>) => {
    if (container?.current) {
        const rect = container.current.getBoundingClientRect();
        return {
            width: rect.width,
            height: rect.height,
            left: rect.left,
            top: rect.top,
        };
    }

    if (typeof window === 'undefined') {
        return {
            width: 0,
            height: 0,
            left: 0,
            top: 0,
        };
    }

    return {
        width: window.innerWidth,
        height: window.innerHeight,
        left: 0,
        top: 0,
    };
};

const useWindowDimensions = (container?: RefObject<HTMLDivElement>) => {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions(container));

    useEffect(() => {
        const handleResize = () => {
            setWindowDimensions(getWindowDimensions(container));
        };

        // requestanimationframe update for the container
        const requestAnimationFrameUpdate = () => {
            handleResize();
            requestAnimationFrame(requestAnimationFrameUpdate);
        };

        const animationFrameId = requestAnimationFrame(requestAnimationFrameUpdate);

        return () => cancelAnimationFrame(animationFrameId);
    }, [container]);

    return windowDimensions;
};

const animatedSpringConfig = {
    mass: 1.5,
    tension: 180,
    friction: 30,
};

const initialPaintStyles = {
    insetInlineStart: 0,
    top: 0,
    transformOrigin: 'top left',
};

const initialLayerStyles = {
    transform: 'scale(1, 1)',
    willChange: 'transform, opacity, inset',
    pointerEvents: 'none',
    opacity: 0,
    zIndex: -1,
    ...initialPaintStyles,
    x: '-100%',
};

const initialTriggerStyles = {
    transform: 'scale(1, 1)',
    pointerEvents: 'auto',
    willChange: 'transform, opacity, inset, filter',
    opacity: 1,
    position: 'relative',
    filter: 'blur(0px)',
    zIndex: 0,
    ...initialPaintStyles,
};

let instances = 0;

const useLayerGenerator = (
    layerProps: {
        content: ReactElement | null;
    },
    container?: RefObject<HTMLDivElement>,
    nbTriggers = 1,
    exitCallback?: () => void,
) => {
    const [isOpen, setIsOpen] = useState(false);
    const lastKnownTriggerBounds = useRef({ top: 0, left: 0, width: 0, height: 0 });
    const lastKnownRef = useRef(null);
    const dimensions = useWindowDimensions(container);
    const [layerStyles, layerStylesAPI] = useSpring(() => ({
        from: initialLayerStyles,
        immediate: true,
    }));

    const [triggerStyles, triggerStylesAPI] = useSprings(nbTriggers, () => ({
        from: initialTriggerStyles,
        immediate: true,
    }));
    const { content } = layerProps;
    if (!content)
        return {
            layer: null,
            openLayer: () => {},
            closeLayer: () => {},
            triggerStyles: [],
            target: null,
        };

    const openLayer = (rect: DOMRect | null | 'outside', index = 0) => {
        const bounds =
            rect === 'outside'
                ? {
                      top: 0,
                      left: -1 * dimensions.width,
                      height: dimensions.height,
                      width: dimensions.width,
                  }
                : rect;
        if (!bounds) return;
        lastKnownTriggerBounds.current = bounds;
        setIsOpen(true);

        const containerOffset = container?.current?.getBoundingClientRect() || { left: 0, top: 0 };
        const relativeLeft = bounds.left - containerOffset.left;
        const relativeTop = bounds.top - containerOffset.top;

        triggerStylesAPI.start((i) => {
            if (i === index) {
                return {
                    from: { ...initialTriggerStyles, zIndex: 10 },
                    to: {
                        insetInlineStart: -1 * relativeLeft,
                        top: -1 * relativeTop,
                        transform: `scale(${dimensions.width / bounds.width}, ${dimensions.width / bounds.width})`,
                        pointerEvents: 'none',
                        opacity: 0,
                        filter: 'blur(10px)',
                        transformOrigin: 'top left',
                        position: 'relative',
                        zIndex: 10,
                    },
                    config: animatedSpringConfig,
                };
            }
        });

        layerStylesAPI.start({
            from: {
                insetInlineStart: bounds.left,
                top: bounds.top,
                transform: `scale(${bounds.width / dimensions.width}, ${bounds.height / dimensions.height})`,
                pointerEvents: 'none',
                opacity: 1,
                transformOrigin: 'top left',
                zIndex: 1 + instances,
                x: '0%',
            },
            to: {
                insetInlineStart: dimensions.left,
                top: dimensions.top,
                transform: 'scale(1, 1)',
                pointerEvents: 'auto',
                opacity: 1,
                transformOrigin: 'top left',
                zIndex: 1 + instances,
            },
            config: animatedSpringConfig,
        });
        instances += 1;
    };

    const closeLayer = () => {
        setIsOpen(false);
        layerStylesAPI.start({
            to: {
                insetInlineStart: lastKnownTriggerBounds.current.left,
                top: lastKnownTriggerBounds.current.top,
                transform: `scale(${lastKnownTriggerBounds.current.width / dimensions.width}, ${
                    lastKnownTriggerBounds.current.height / dimensions.height
                })`,
                pointerEvents: 'none',
                opacity: 0,
                transformOrigin: 'top left',
                zIndex: -1,
            },
            config: animatedSpringConfig,
        });
        triggerStylesAPI.start({
            to: initialTriggerStyles,
            onRest: () => {
                layerStylesAPI.start({
                    to: initialLayerStyles,
                    onRest: () => {
                        exitCallback?.();
                    },
                });
            },
            config: animatedSpringConfig,
        });
        instances = Math.max(instances - 1, 0);
    };

    return {
        layer: (
            <animated.div
                className={cn('fixed flex', !isOpen && 'recursive-pointer-events-none')}
                style={{
                    width: dimensions.width,
                    height: dimensions.height,
                    left: dimensions.left,
                    top: dimensions.top,
                    ...(layerStyles as unknown as CSSProperties),
                }}
            >
                {content && cloneElement(content, { closeLayer })}
            </animated.div>
        ),
        openLayer,
        closeLayer,
        isOpen,
        triggerStyles,
        target: lastKnownRef.current,
    };
};

export default useLayerGenerator;
